import type { ReactNode } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { useDict } from '../i18n/dictionary';
import { shell } from '../i18n/shell';

/**
 * One `NAV` per app drives both menus: the sidebar from lg up, and below it a
 * bottom bar with one tab per top-level entry. A group therefore needs its own
 * `href` and `icon` — in the bar it collapses into a single tab, and its
 * children are only listed in the sidebar.
 */

export interface NavLeaf {
  key: string;
  label: string;
  href: string;
}

export interface NavItem extends NavLeaf {
  icon: () => ReactNode;
  /** Match `href` exactly instead of as a prefix — for the app's root tab. */
  end?: boolean;
  /** Extra route patterns that also light the entry up. */
  activeOn?: string[];
}

export interface NavGroup extends NavItem {
  children: NavLeaf[];
}

export type NavEntry = NavItem | NavGroup;

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'children' in entry;
}

/**
 * A tab must always be lit, so unlike `NavLink`'s default this is a prefix
 * match: `/finances/wallets/:id` would still belong to the Wallets tab.
 */
function useIsActive(): (item: NavLeaf & Partial<NavItem>) => boolean {
  const { pathname } = useLocation();
  return (item) =>
    matchPath({ path: item.href, end: item.end ?? false }, pathname) !== null ||
    (item.activeOn ?? []).some((pattern) => matchPath(pattern, pathname));
}

function navLinkClass(active: boolean, nested: boolean): string {
  return [
    'nav-link',
    nested ? 'nav-link-nested' : '',
    active ? 'is-active' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

export function Sidebar({ items }: { items: NavEntry[] }) {
  const isActive = useIsActive();
  /* Children are leaves, matched exactly — the sidebar is the one place a
     script page should not also light up its siblings. */
  const leafActive = (leaf: NavLeaf) => isActive({ ...leaf, end: true });

  return (
    <nav className="nav-stack">
      {items.map((entry) =>
        isGroup(entry) ? (
          <div key={entry.key}>
            <div className="nav-group-label">{entry.label}</div>
            {entry.children.map((child) => (
              <Link
                key={child.key}
                to={child.href}
                className={navLinkClass(leafActive(child), true)}
                aria-current={leafActive(child) ? 'page' : undefined}
              >
                {child.label}
              </Link>
            ))}
          </div>
        ) : (
          <Link
            key={entry.key}
            to={entry.href}
            className={navLinkClass(isActive(entry), false)}
            aria-current={isActive(entry) ? 'page' : undefined}
          >
            {entry.label}
          </Link>
        ),
      )}
    </nav>
  );
}

function BottomBar({ items }: { items: NavEntry[] }) {
  const isActive = useIsActive();
  const t = useDict(shell);

  return (
    <nav className="bottom-bar" aria-label={t.appMenu}>
      {items.map((entry) => {
        const active = isActive(entry);
        const Icon = entry.icon;
        return (
          <Link
            key={entry.key}
            to={entry.href}
            className={active ? 'bottom-tab is-active' : 'bottom-tab'}
            aria-current={active ? 'page' : undefined}
          >
            <span className="bottom-tab-pill">
              <Icon />
              <span className="bottom-tab-label">{entry.label}</span>
            </span>
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * Sidebar beside the page body from lg up (desktop, tablet in landscape),
 * bottom bar under it below (phone, tablet in portrait).
 */
export function AppShellGrid({
  items,
  children,
}: {
  items: NavEntry[];
  children: React.ReactNode;
}) {
  return (
    <section className="page-wide page-with-bottom-bar">
      <div className="app-shell-grid">
        <aside className="sidebar">
          <Sidebar items={items} />
        </aside>
        <div>{children}</div>
      </div>
      <BottomBar items={items} />
    </section>
  );
}
